import React, { useEffect, useMemo, useState } from 'react';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { useUsersStore } from '../../../store/usersStore';
import { User, UserRole } from '../../../services/usersApi';
import { UsersTable, UserModal } from './components';
import styles from './AdminUsers.module.css';

const ROLE_FILTERS: Array<UserRole | 'All'> = [
  'All', 'Admin', 'Sales Rep', 'Driver', 'Warehouse Manager', 'Call Center',
];

/**
 * Admin → Users — staff user management. Customer onboarding has its own
 * dedicated flow (admin/AdminOnboardingDashboard); this screen is for
 * Admin / Sales Rep / Driver / Warehouse Manager / Call Center accounts only.
 */
const AdminUsers: React.FC = () => {
  const items = useUsersStore((s) => s.items);
  const loading = useUsersStore((s) => s.loading);
  const error = useUsersStore((s) => s.error);
  const fetchList = useUsersStore((s) => s.fetchList);
  const deactivate = useUsersStore((s) => s.deactivate);
  const activate = useUsersStore((s) => s.activate);
  const remove = useUsersStore((s) => s.remove);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'All'>('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Exclude Customer rows entirely — they're managed via the dedicated
  // onboarding flow, not this admin staff screen.
  const staffOnly = useMemo(() => items.filter((u) => u.role !== 'Customer'), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staffOnly.filter((u) => {
      const matchesRole = roleFilter === 'All' || u.role === roleFilter;
      const matchesQuery = !q
        || u.full_name.toLowerCase().includes(q)
        || u.email.toLowerCase().includes(q)
        || (u.employee_id ?? '').toLowerCase().includes(q);
      return matchesRole && matchesQuery;
    });
  }, [staffOnly, search, roleFilter]);

  const stats = useMemo(() => {
    const byRole: Record<string, number> = {};
    let active = 0;
    for (const u of staffOnly) {
      byRole[u.role] = (byRole[u.role] ?? 0) + 1;
      if (u.is_active) active++;
    }
    return {
      total: staffOnly.length,
      active,
      inactive: staffOnly.length - active,
      byRole,
    };
  }, [staffOnly]);

  const handleToggleActive = async (id: number) => {
    const u = staffOnly.find((x) => x.id === id);
    if (!u) return;
    const verb = u.is_active ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${verb} ${u.full_name}?`)) return;
    try {
      if (u.is_active) await deactivate(id);
      else await activate(id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : `Failed to ${verb.toLowerCase()} user`);
    }
  };

  const handleDelete = async (id: number) => {
    const u = staffOnly.find((x) => x.id === id);
    if (!u) return;
    if (!window.confirm(`Permanently delete ${u.full_name}? This cannot be undone.`)) return;
    try {
      await remove(id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Users</h2>
          <p className={styles.subtitle}>
            Manage staff accounts (Admin, Sales Rep, Driver, Warehouse Manager, Call Center).
            Customer accounts are managed through the onboarding flow.
          </p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          Add User
        </button>
      </div>

      <div className={styles.statsGrid}>
        <StatCard label="Total Staff" value={String(stats.total)} tone="blue" />
        <StatCard label="Active" value={String(stats.active)} tone="green" />
        <StatCard label="Inactive" value={String(stats.inactive)} tone="red" />
        <StatCard label="Admins" value={String(stats.byRole['Admin'] ?? 0)} tone="purple" />
      </div>

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search by name, email, or employee ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.rolePills}>
          {ROLE_FILTERS.map((r) => (
            <button
              key={r}
              type="button"
              className={`${styles.pill} ${roleFilter === r ? styles.pillActive : ''}`}
              onClick={() => setRoleFilter(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={false}
        onRetry={() => fetchList()}
      >
        <UsersTable
          items={filtered}
          onEdit={(id) => {
            const u = staffOnly.find((x) => x.id === id);
            if (u) setEditing(u);
          }}
          onToggleActive={handleToggleActive}
          onDelete={handleDelete}
        />
      </AsyncBoundary>

      <UserModal
        open={showAddModal}
        mode="add"
        onClose={() => setShowAddModal(false)}
      />
      <UserModal
        open={!!editing}
        mode="edit"
        existing={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; tone: 'blue' | 'green' | 'red' | 'purple' }> = ({ label, value, tone }) => (
  <div className={`${styles.statCard} ${styles[`tone_${tone}`]}`}>
    <span className={styles.statLabel}>{label}</span>
    <span className={styles.statValue}>{value}</span>
  </div>
);

export const AdminUsersPage: React.FC = () => <AdminUsers />;
export { AdminUsers };
export default AdminUsers;
